import api from "../../config/Api";
import { Order, OrderItem, OrderState } from "../../types/orderTypes";
import { Address } from "../../types/userTypes";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

const initialState: OrderState = {
    orders: [],
    orderItem: null,
    currentOrder: null,
    paymentOrder: null,
    loading: false,
    error: null,
    orderCancelled: false,
};

const API_URL = "/api/orders";

export const fetchUserOrderHistory = createAsyncThunk<Order[], string>(
    "orders/fetchUserOrderHistory",
    async (jwt, {rejectWithValue}) => {
        try {
            const response = await api.get<Order[]>(`${API_URL}/user`, {
                headers: {Authorization: `Bearer ${jwt}`},
            });
            //console.log("order history fetched ", response.data);
            return response.data;
        } catch (error: any) {
            //console.log("error ", error.response);
            return rejectWithValue("Failed to fetch order");
        }
    }
);

export const fetchOrderById = createAsyncThunk<Order, {orderId: number; jwt: string}>(
    "orders/fetchOrderById",
    async ({orderId, jwt}, {rejectWithValue}) =>{
        try {
            const response = await api.get(`${API_URL}/${orderId}`, {
                headers: {Authorization: `Bearer ${jwt}`},
            });
            //console.log("order fetched ", response.data);
            return response.data;
        } catch (error: any) {
           // console.log("error ", error.response);
            return rejectWithValue("Failed to fetch order");
        }
    }
);

export const createOrder = createAsyncThunk<
  any, 
  { address: Address; jwt: string; paymentGateway: string; phoneNumber?: string }
>(
  "orders/createOrder",
  async ({ address, jwt, paymentGateway, phoneNumber }, { rejectWithValue }) => {
    try {
      const response = await api.post<any>(
        API_URL, 
        { address, phoneNumber }, 
        {
          headers: { 
            Authorization: `Bearer ${jwt}`,
            "Accept": "application/json",
            "Content-Type": "application/json"
          },
          params: { paymentMethod: paymentGateway } 
        }
      );
      
     // console.log("order created: ", response.data);
      return response.data;
    } catch (error: any) {
      //console.log("error ", error.response);
      return rejectWithValue("Failed to create order");
    }
  }
);

export const fetchOrderItemById = createAsyncThunk<OrderItem, { orderItemId: number; jwt: string}> (
    "orders/fetchOrderItemById",
    async ({ orderItemId, jwt}, { rejectWithValue }) => {
        try {
            const response = await api.get<OrderItem>(`${API_URL}/item/${orderItemId}`, {
                headers: {Authorization: `Bearer ${jwt}`},
            });
            //console.log("order item fetched ", response.data);
            return response.data;
        } catch (error: any) {
            //console.log("error ", error.response);
            return rejectWithValue("Failed to fetch order by item id");
        }
    }
);

export const paymentSuccess = createAsyncThunk<any, { paymentId: string; jwt: string, paymentLinkId: string}, {rejectValue: string}>(
    "orders/paymentSuccess",
    async ({paymentId, jwt, paymentLinkId}, {rejectWithValue}) => {
        try {
            const response = await api.get(`/api/payment/${paymentId}`, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                },
                params: {paymentLinkId}
            });
            //console.log("payment sucess ", response.data);
            return response.data;
        } catch (error: any) {
            //console.log("error ", error.response);
            if (error.response){
                return rejectWithValue(error.response.data.messsage);
            }
            return rejectWithValue("failed to process payment");
        }
    }
);


export const cancelOrder = createAsyncThunk<Order, { orderId: number; jwt: string }>(
    "orders/cancelOrder",
    async ({ orderId, jwt }, { rejectWithValue }) => {
        try {
            const response = await api.put(`${API_URL}/${orderId}/cancel`, {}, {
                headers: {
                    Authorization: `Bearer ${jwt}`,
                },
            });
            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue("An error occurred when cancelling the order.");
        }
    }
);

const orderSlice = createSlice({
    name:"orders",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder

        .addCase(fetchUserOrderHistory.pending, (state) => {
            state.loading = true;
            state.error = null;
            state.orderCancelled = false;
        })
        .addCase(fetchUserOrderHistory.fulfilled, (state, action: PayloadAction<Order[]>) => {
            state.orders = action.payload;
            state.loading = false;
        })
        .addCase(fetchUserOrderHistory.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
        .addCase(fetchOrderById.pending, (state) => {
            state.loading = true;
            state.error= null;
        })
        .addCase(fetchOrderById.fulfilled, (state, action: PayloadAction<Order>) => {
            state.currentOrder = action.payload;
            state.loading = false;
        })
        .addCase(fetchOrderById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
        .addCase(createOrder.pending, (state) => {
            state.loading = true;
            state.error= null;
        })
        .addCase(createOrder.fulfilled, (state, action: PayloadAction<any>) => {
            state.currentOrder = action.payload;
            state.loading = false;
        })
        .addCase(createOrder.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
        .addCase(fetchOrderItemById.pending, (state) => {
            state.loading = true;
            state.error= null;
        })
        .addCase(fetchOrderItemById.fulfilled, (state, action) => {
            state.loading = false;
            state.orderItem = action.payload;
        })
        .addCase(fetchOrderItemById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
        .addCase(paymentSuccess.pending, (state) => {
            state.loading = true;
            state.error= null;
        })
        .addCase(paymentSuccess.fulfilled, (state, action) => {
            state.loading = false;
            console.log("Payment successful: ", action.payload);
        })
        .addCase(paymentSuccess.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
        .addCase(cancelOrder.pending, (state) => {
            state.loading = true;
            state.error= null;
            state.orderCancelled = false;
        })
        .addCase(cancelOrder.fulfilled, (state, action) => {
            state.loading = false;
            state.orders = state.orders.map((order) =>
                order.id === action.payload.id ? action.payload : order
            );
            state.orderCancelled = true;
            state.currentOrder = action.payload
        })
        .addCase(cancelOrder.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
    },
});

export default orderSlice.reducer;